"use client";

import { LayoutDashboard, Plus, RefreshCw, Save, XCircle } from "lucide-react";
import { useCallback } from "react";

import { useAppDialogs } from "@/components/dialogs/app-dialog-provider";
import { Button } from "@/components/ui-system";
import { useDashboardBuilderOptional } from "@/contexts/DashboardBuilderContext";
import { useDebouncedWidgetRefresh } from "@/hooks/useWidgetData";

import { useDashboardRuntime } from "./DashboardRuntimeContext";
import type { TimeRangeKey } from "./widget-config";

const RANGE_OPTIONS: { value: TimeRangeKey; label: string }[] = [
  { value: "last_1h", label: "Last 1h" },
  { value: "last_24h", label: "Last 24h" },
  { value: "last_7d", label: "Last 7d" },
];

type DashboardToolbarProps = {
  dashboardName?: string | null;
  showTimeRangeControl?: boolean;
  showEditToggle?: boolean;
  showAddWidget?: boolean;
  showSaveActions?: boolean;
};

export function DashboardToolbar({
  dashboardName,
  showTimeRangeControl = true,
  showEditToggle = true,
  showAddWidget = true,
  showSaveActions = true,
}: DashboardToolbarProps) {
  const rt = useDashboardRuntime();
  const builder = useDashboardBuilderOptional();
  const dialogs = useAppDialogs();
  const debouncedRefresh = useDebouncedWidgetRefresh(450);

  const onRefresh = useCallback(() => {
    debouncedRefresh();
  }, [debouncedRefresh]);

  return (
    <div className="mb-ds-6 flex flex-col gap-ds-4 rounded-xl border border-card-border bg-card px-ds-4 py-ds-4 shadow-card md:flex-row md:items-center md:justify-between">
      <div className="min-w-0">
        <p className="text-micro font-semibold uppercase tracking-[0.12em] text-primary">Dashboard</p>
        <p className="truncate text-caption font-semibold text-foreground">
          {dashboardName ?? "Primary layout"}
        </p>
        <p className="mt-ds-1 text-caption text-muted">
          Use the page range control to override widget defaults. Refresh debounces duplicate fetches.
        </p>
        {builder?.isDirty ? (
          <p className="mt-ds-2 text-micro font-medium text-amber-700 dark:text-amber-400">Unsaved changes</p>
        ) : null}
        {builder?.saveError ? (
          <p className="mt-ds-2 text-micro text-error" role="alert">
            {builder.saveError}
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-ds-3">
        {builder ? (
          <>
            {showEditToggle ? (
              <Button
                type="button"
                variant={builder.editMode ? "primary" : "secondary"}
                size="sm"
                className="gap-ds-2"
                onClick={() => void builder.setEditMode(!builder.editMode)}
                title="Toggle dashboard builder"
              >
                <LayoutDashboard className="h-4 w-4" aria-hidden />
                {builder.editMode ? "Done" : "Edit"}
              </Button>
            ) : null}
            {builder.editMode ? (
              <>
                {showAddWidget ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="gap-ds-2"
                    onClick={() => builder.setAddModalOpen(true)}
                  >
                    <Plus className="h-4 w-4" aria-hidden />
                    Add widget
                  </Button>
                ) : null}
                {showSaveActions && builder.isDirty ? (
                  <>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      className="gap-ds-2"
                      disabled={builder.saving}
                      onClick={() => void builder.saveAll()}
                    >
                      <Save className="h-4 w-4" aria-hidden />
                      {builder.saving ? "Saving…" : "Save changes"}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="gap-ds-2"
                      disabled={builder.saving}
                      onClick={async () => {
                        if (
                          builder.isDirty &&
                          !(await dialogs.confirm({
                            title: "Discard unsaved changes?",
                            description: "Any widget edits that have not been saved will be lost.",
                            confirmLabel: "Discard changes",
                            tone: "danger",
                          }))
                        ) {
                          return;
                        }
                        builder.clearDirty();
                      }}
                    >
                      <XCircle className="h-4 w-4" aria-hidden />
                      Cancel
                    </Button>
                  </>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}
        {showTimeRangeControl ? (
          <label className="flex items-center gap-ds-2">
            <span className="text-caption font-medium text-muted">Time range</span>
            <select
              className="ui-control min-w-[10rem] py-ds-2 text-caption"
              value={rt?.globalTimeRange?.key ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                rt?.setGlobalTimeRange(v === "" ? null : { key: v as TimeRangeKey });
              }}
              aria-label="Global time range"
            >
              <option value="">Widget default</option>
              {RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="gap-ds-2"
          onClick={onRefresh}
          title="Refresh all widget data"
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          Refresh
        </Button>
      </div>
    </div>
  );
}
